import { Entypo } from "@expo/vector-icons";
import React, { useContext, useMemo } from "react";
import { StyleSheet, TouchableWithoutFeedback, View } from "react-native";
import { useTheme } from "react-native-elements";
import { getUnlockedDungeons } from "../../backend/exploration/dungeons/Dungeons";
import {
  BestiaryState,
  Enemy,
  getCumulativeEnemyKills,
  getCumulativeEnemyKillsFromCreatures,
} from "../../backend/exploration/enemies/Enemy";
import { getItemById } from "../../backend/items/Items";
import { PlayerContext } from "../../backend/PlayerContext";
import { formatNumber, RoundingMethod } from "../../utils/FormattingUtils";
import { EnemySpriteTile } from "../exploration/EnemySpriteTile";
import CategoryContainer from "./CategoryContainer";
import { EnemyCombatStatsViewer } from "./CombatStatsViewer";
import { Markdown } from "./Markdown";
import { Text } from "./Text";

export function BestiaryEnemyContainer(props: {
  enemy: Enemy;
  collapsed?: boolean;
  onPressExpand?: () => void;
}) {
  const enemy = props.enemy;
  const id = enemy.getId();
  const name = enemy.getName();
  const playerContext = useContext(PlayerContext);

  const unlockedFloors = getUnlockedDungeons(playerContext)
    .map((dungeon) =>
      dungeon
        .getDungeonFloors()
        .filter((floor) => floor.isUnlocked(playerContext)),
    )
    .flat();

  const bestiaryState = enemy.getBestiaryState(playerContext);

  const foundInDungeons = unlockedFloors
    .filter((floor) =>
      floor
        .getExplorationPossibilities(playerContext)
        .find((possibility) => possibility.transforms?.enemy == enemy),
    )
    .map((floor) => {
      const possibilities = floor.getExplorationPossibilities(playerContext);
      const weight =
        possibilities.find(
          (possibility) => possibility.transforms?.enemy == enemy,
        )?.weight ?? 0;
      const totalWeight = possibilities.reduce(
        (prev, current) => prev + current.weight,
        0,
      );
      return `*${floor.getFloorName()}*: ${formatNumber(
        (weight * 100) / totalWeight,
      )}%`;
    })
    .concat(
      unlockedFloors
        .filter((floor) => floor.getBoss(playerContext) == enemy)
        .map((floor) => `*${floor.getFloorName()}* (Boss)`),
    )
    .join(", ");

  const showDrops = bestiaryState == BestiaryState.Loot;

  const dropsText = [
    enemy.getCoinsAwarded(playerContext) > 0
      ? `${formatNumber(enemy.getCoinsAwarded(playerContext))}:coins:`
      : null,
    enemy.getMonstiumAwarded(playerContext) > 0
      ? `${formatNumber(enemy.getMonstiumAwarded(playerContext))}:monstium:`
      : null,
  ]
    .concat(
      enemy.getItemsAwarded(playerContext).map((loot) => {
        if (loot.chance == 0) {
          return null;
        }
        const item = getItemById(loot.itemId);
        return `${loot.amount}x:${loot.itemId}:*${item.getName(
          loot.params ?? item.getDefaultParams(),
        )}* (${formatNumber(loot.chance * 100, {
          showDecimals: true,
          roundingMethod: RoundingMethod.UP,
        })}%)`;
      }),
    )
    .filter((v) => v != null)
    .join(", ");

  const cumulativeKills = getCumulativeEnemyKills(playerContext, enemy.getId());
  const cumulativeCreatureKills = getCumulativeEnemyKillsFromCreatures(
    playerContext,
    enemy.getId(),
  );

  const generalText = `Times defeated: *${formatNumber(cumulativeKills)}${cumulativeCreatureKills > 0
    ? ` (${formatNumber(cumulativeCreatureKills)} from creatures)`
    : ""
    }*`;

  const enemyContentContainer = useMemo(() => {
    return (
      <View style={styles.contentContainer}>
        <View style={styles.row}>
          <View style={styles.column}>
            <EnemySpriteTile sprite={enemy.getPicture(playerContext)} />
          </View>
          <View style={styles.column}>
            <CategoryContainer title="Base stats" disableExpand={true}>
              <View style={styles.categoryContents}>
                <EnemyCombatStatsViewer enemy={enemy} />
              </View>
            </CategoryContainer>
            <CategoryContainer title="General" disableExpand={true}>
              <View style={styles.categoryContents}>
                <Markdown>{generalText}</Markdown>
              </View>
            </CategoryContainer>
          </View>
        </View>
        <View style={styles.row}>
          <View style={styles.column}>
            <CategoryContainer title="Found in" disableExpand={true}>
              <View style={styles.categoryContents}>
                <Markdown>
                  {foundInDungeons != "" ? foundInDungeons : "???"}
                </Markdown>
              </View>
            </CategoryContainer>
          </View>
          <View style={styles.column}>
            <CategoryContainer title="Base loot" disableExpand={true}>
              <View style={styles.categoryContents}>
                <Markdown>
                  {!showDrops
                    ? "You must defeat this enemy more times to see loot information!"
                    : dropsText != ""
                      ? dropsText
                      : "No loot"}
                </Markdown>
              </View>
            </CategoryContainer>
          </View>
        </View>
      </View >
    );
  }, [enemy, foundInDungeons, dropsText, generalText]);

  const theme = useTheme();

  return useMemo(() => {
    return (
      <View style={styles.helpContainer} key={id}>
        <TouchableWithoutFeedback
          accessibilityRole="button"
          accessibilityHint="Expand"
          onPress={props.onPressExpand}
        >
          <View style={styles.topRowContainer}>
            <Text h3 key={id} style={[styles.topContainer]}>
              {name + (enemy.isBoss() ? " (Boss)" : "")}
            </Text>
            <View style={styles.expandContainer}>
              <Entypo
                name={props.collapsed ? "chevron-right" : "chevron-down"}
                size={24}
                color={theme.theme.colors.black}
              />
            </View>
          </View>
        </TouchableWithoutFeedback>
        {!props.collapsed && <View>{enemyContentContainer}</View>}
      </View>
    );
  }, [
    theme,
    name,
    id,
    !props.collapsed && enemyContentContainer,
    props.collapsed,
    props.onPressExpand,
  ]);
}

const styles = StyleSheet.create({
  helpContainer: { marginVertical: 8 },
  topRowContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  expandContainer: {
    width: 40,
  },
  descriptionTextContainer: {
    marginBottom: 8,
    marginTop: 8,
    marginHorizontal: 8,
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  descriptionViewer: { flex: 1, justifyContent: "center" },
  topContainer: {
    flex: 1,
    marginLeft: 8,
  },
  disabledStorylineText: {
    color: "#aaa",
  },
  contentContainer: {
    paddingVertical: 8,
  },
  row: {
    flexDirection: "row",
  },
  categoryContents: {
    marginTop: 8,
  },
  column: {
    flex: 1,
    marginHorizontal: 4,
  },
});
